import { FormattedMessage, useIntl } from 'react-intl';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table } from 'components/Table';

import { ExchangeChart } from 'components/ExchangeChart';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'utils/dayjs';
import {
  getCitiesEntities,
  getSortedCitiesItems,
  makeCurrenciesAllTypesRatesIsInitialized,
  makeCurrenciesTypeRatesItems,
  makeCurrentCity,
  makeCurrentCurrency,
} from 'selectors';
import { fetchCurrenciesAllTypesRates } from 'actions';
import ButtonsGroup from 'components/commons/ButtonsGroup';
import Select from 'components/commons/Select';
import Updated from 'components/commons/Updated';
import { useParams, useLocation } from 'react-router-dom';

import Icon from 'components/commons/Icon';
import ExchangersTable from 'components/ExchangersTable';

import {
  DEFAULT_MOBILE_DATE_FORMAT,
  DEFAULT_SOURCE_CURRENCY,
  filterRatesByCurrency,
  getHrefAllExchangers,
  getLocaleUrlPath,
  formatParsedDate,
} from 'utils';

import {
  ButtonsWrapper,
  Wrapper,
  CitiesWrapper,
  SectionHeader,
  Title,
  Source,
  TableFooterLink,
  TableFooter,
  RelativeWrapper,
} from './styles';

const titles = ['сurrency', 'purchase', 'sale'];

const primaryList = [
  {
    value: 'all',
    name: 'button.all',
  },
  {
    value: 'kiev',
    name: 'button.kiev',
  },
  {
    value: 'lvov',
    name: 'button.lvov',
  },
  {
    value: 'zaporozhye',
    name: 'button.zaporozhye',
  },
  {
    value: 'kharkov',
    name: 'button.kharkov',
  },
  {
    value: 'dnepropetrovsk',
    name: 'button.dnepropetrovsk',
  },
  {
    value: 'odessa',
    name: 'button.odessa',
  },
];

const selectedCity = [
  'uzhgorod',
  'svaljava',
  'irshava',
  'beregovo',
  'vinogradov',
  'mukachevo',
  'hust',
  'chop',
];

/**
 * Скільки повторно показувати вже завантажені курси міста, не роблячи новий
 * запит. Збігається з APP_RESPONSE_CACHE_MAX_AGE — тим самим вікном
 * несвіжості, з яким сторінка віддається з кешу.
 */
const RATES_FRESHNESS_TIMEOUT = 5 * 60 * 1000;

const isKnownCitySlug = slug =>
  Boolean(
    slug && slug !== 'all' && primaryList.some(option => option.value === slug)
  );

const CurrencyExchangeTable = () => {
  const intl = useIntl();
  const { formatMessage, locale } = intl;
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { date, city, currency } = useParams();

  const urlCitySlug = city && city !== 'all' ? city : null;

  /**
   * Ініціалізуємо містом з URL, щоб перший запит одразу пішов з потрібним
   * ключем — інакше виходить зайвий запит по «всіх містах».
   */
  const [selectCity, setSelectCity] = useState(() =>
    urlCitySlug ? { value: urlCitySlug } : undefined
  );

  const selectRatesItems = useMemo(makeCurrenciesTypeRatesItems, []);
  const selectRatesIsInitialized = useMemo(
    makeCurrenciesAllTypesRatesIsInitialized,
    []
  );
  const selectCurrentCity = useMemo(makeCurrentCity, []);
  const selectCurrentCurrency = useMemo(makeCurrentCurrency, []);

  const citiesList = useSelector(getSortedCitiesItems);
  const citiesEntities = useSelector(getCitiesEntities);

  const pathSlug = useMemo(
    () => pathname.split('/').filter(Boolean)[0] || null,
    [pathname]
  );

  const requestCitySlug = selectCity?.value || null;

  const requestDate = useMemo(
    () => date || dayjs().format(DEFAULT_MOBILE_DATE_FORMAT),
    [date]
  );

  const ratesParams = useMemo(
    () => ({
      city: requestCitySlug || 'allCities',
      date: requestDate,
      type: 'auction',
    }),
    [requestCitySlug, requestDate]
  );

  const currenciesAuctionRatesItems = useSelector(state =>
    selectRatesItems(state, ratesParams)
  );

  const isRatesInitialized = useSelector(state =>
    selectRatesIsInitialized(state, ratesParams)
  );

  const currentCurrency = useSelector(state =>
    selectCurrentCurrency(state, { currency })
  );

  const hrefForSelectedCities =
    locale === 'uk'
      ? 'https://currency.minfin.app/applink/CXibkSuqWgVvk4TZA'
      : 'https://currency.minfin.app/applink/q7UFWXyMuq29WrZe6';

  const data = filterRatesByCurrency(currenciesAuctionRatesItems, currency);

  const currentCityByUrl = useSelector(state =>
    selectCurrentCity(state, { city: city || pathSlug })
  );

  const resolvedCitySlug =
    (selectCity?.value && selectCity.value !== 'all'
      ? selectCity.value
      : null) ||
    (city && city !== 'all' ? city : null) ||
    currentCityByUrl?.value ||
    (pathSlug && (citiesEntities[pathSlug] || isKnownCitySlug(pathSlug))
      ? pathSlug
      : null);

  const getButtonHref = rowCurrency => {
    const isSelectedCity = selectedCity.find(
      citySlug => citySlug === selectCity?.value
    );

    if (isSelectedCity) {
      return hrefForSelectedCities;
    }

    if (resolvedCitySlug) {
      return getHrefAllExchangers({
        locale,
        currency: rowCurrency,
        city: resolvedCitySlug,
      });
    }

    return `https://minfin.com.ua${getLocaleUrlPath(
      locale
    )}/currency/auction/${rowCurrency}/buy/all/`;
  };

  const exchangersUpdated = data[0]?.date || dayjs();

  const placeholder = useMemo(
    () => formatMessage({ id: 'searchCurr.placeholder' }),
    []
  );

  const hasRatesData = Object.keys(currenciesAuctionRatesItems).length > 0;

  const requestKey = `${ratesParams.city}|${date || ''}|${currency ||
    ''}|${locale}`;

  const requestedAtRef = useRef({});
  const isFirstFetchRunRef = useRef(true);

  useEffect(() => {
    const isFirstRun = isFirstFetchRunRef.current;
    isFirstFetchRunRef.current = false;

    /**
     * На першому рендері дані вже можуть бути з SSR — тоді запит зайвий.
     * Порожній результат означає, що запит на сервері не вдався, і його
     * треба повторити на клієнті.
     */
    if (isFirstRun && isRatesInitialized && hasRatesData) {
      requestedAtRef.current[requestKey] = Date.now();
      return;
    }

    const requestedAt = requestedAtRef.current[requestKey];

    if (requestedAt && Date.now() - requestedAt < RATES_FRESHNESS_TIMEOUT) {
      return;
    }

    requestedAtRef.current[requestKey] = Date.now();

    dispatch(
      fetchCurrenciesAllTypesRates({
        city: requestCitySlug,
        date: date || null,
        currency: currency || null,
        locale,
      })
    );
  }, [requestKey]);

  const getHref = currency => {
    const getCurrency = `${currency}/`;
    const getCity = city ? `${city}/` : '';
    const getDate = date ? `${date}/` : '';

    return `/${getCity}${getCurrency}${getDate}`;
  };

  useEffect(() => {
    if (currentCityByUrl) {
      setSelectCity(currentCityByUrl);
    }
  }, [currentCityByUrl]);

  const handleOnClick = value => {
    if (value === 'all') {
      setSelectCity(undefined);

      return;
    }

    const currentCity = citiesList.find(item => item.value === value);

    setSelectCity(currentCity || { value });
  };

  const displayOptions = useMemo(() => {
    /**
     * Поки місто з URL не зіставлене зі списком, підпису для кнопки немає —
     * показуємо базовий список, щоб не рендерити порожню кнопку.
     */
    if (!selectCity?.label) {
      return primaryList;
    }
    if (primaryList.find(option => option.value === selectCity?.value)) {
      return primaryList;
    }
    const mainOptions = primaryList.slice(0, primaryList.length - 1);
    return [
      ...mainOptions,
      { value: selectCity?.value, label: selectCity?.label },
    ];
  }, [selectCity, primaryList]);

  return (
    <Wrapper id="CurrencyExchangeTable">
      <SectionHeader>
        <Title>
          <FormattedMessage
            id="table.exchange.mainTitle"
            values={{
              currency: currentCurrency?.nameG,
              isDate: date ? 1 : 0,
              isCity: selectCity?.nameG ? 1 : 0,
              city: selectCity?.nameG,
              date: formatParsedDate(date, 'DD MMMM YYYY', locale),
            }}
          />
        </Title>

        {!date && <Updated date={exchangersUpdated} />}
      </SectionHeader>

      <CitiesWrapper>
        <RelativeWrapper>
          <ButtonsWrapper>
            <ButtonsGroup
              buttons={displayOptions}
              onClick={handleOnClick}
              selected={selectCity?.value || 'all'}
            />

            <Select
              options={citiesList}
              isFilterable
              type="outline"
              isButtonMode
              onChange={handleOnClick}
              position="fixed"
            />
          </ButtonsWrapper>
        </RelativeWrapper>
      </CitiesWrapper>

      <Table
        titles={titles}
        rows={data}
        placeholder={placeholder}
        type="exchanger"
        anotherCurrTable={false}
        getHref={getHref}
        activeCurrency={currency}
        buttonHref={getButtonHref}
      />

      <TableFooter>
        <TableFooterLink
          href={
            resolvedCitySlug
              ? getHrefAllExchangers({
                  locale,
                  currency: currency || DEFAULT_SOURCE_CURRENCY,
                  city: resolvedCitySlug,
                })
              : `https://minfin.com.ua${getLocaleUrlPath(
                  locale
                )}/currency/auction/`
          }
        >
          <FormattedMessage id="exchangeRates.moreButton" />
          <Icon name="vectorStroke" />
        </TableFooterLink>

        <Source>
          <FormattedMessage id="source.average" />
        </Source>
      </TableFooter>

      <ExchangeChart
        type="exchanger"
        chartTitle="dynamic.rates.chart.exchange.title"
      />
      <ExchangersTable />
    </Wrapper>
  );
};

export default CurrencyExchangeTable;
